import { DateTime } from 'luxon';

/**
 * Helpers for the Cache Management card, which reads `GET /v1/queues/stats`.
 *
 * Note that nothing in that payload says how stale the cached data actually is —
 * `backlogAgeSeconds` is the age of the oldest *queued* item, not how far the cache
 * trails reality. So a queue with work outstanding reads as "Behind" with no duration
 * attached; the backlog age is reported on its own as a plain metric.
 */

export type QueueTone = 'ok' | 'warn' | 'error';

/**
 * A backlog past this point stops reading as "busy" and starts reading as "something is
 * wrong". Rebuilds are expected to take minutes, so this sits well above a normal run.
 */
export const QUEUE_BACKLOG_ERROR_SECONDS = 600;

export interface QueueStatus {
  tone: QueueTone;
  label: string;
}

/** Stuck work outranks backlog: a drained queue holding stuck items still needs a human. */
export function queueStatus(stats: { pending: number; backlogAgeSeconds: number; stuck?: number }): QueueStatus {
  if (stats.stuck && stats.stuck > 0) {
    return { tone: 'error', label: `${stats.stuck.toLocaleString()} stuck — needs attention` };
  }

  if (stats.pending === 0 && stats.backlogAgeSeconds === 0) {
    return { tone: 'ok', label: 'Caught up' };
  }

  return {
    tone: stats.backlogAgeSeconds >= QUEUE_BACKLOG_ERROR_SECONDS ? 'error' : 'warn',
    label: 'Behind',
  };
}

/** Coarsens as it grows: `45s`, `4m 12s`, `1h 45m`. Exact seconds stop being useful past an hour. */
export function formatQueueDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds || 0));

  if (total < 60) return `${total}s`;

  const totalMinutes = Math.floor(total / 60);

  if (totalMinutes < 60) {
    const remainderSeconds = total % 60;
    return remainderSeconds === 0 ? `${totalMinutes}m` : `${totalMinutes}m ${remainderSeconds}s`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainderMinutes = totalMinutes % 60;
  return remainderMinutes === 0 ? `${hours}h` : `${hours}h ${remainderMinutes}m`;
}

/**
 * `oldestPendingAt` is null whenever the queue is empty, which reads as "none". Otherwise
 * it renders in the viewer's own zone, labelled with it (`09:02:11 AM CDT`) — these get
 * pasted into tickets and read by people in other offices, so a bare wall-clock time is
 * ambiguous. Zones without an abbreviation fall back to an offset (`GMT+5:30`).
 */
export function formatQueueTimestamp(iso: string | null | undefined): string {
  if (!iso) return 'none';

  const timestamp = DateTime.fromISO(iso);
  return timestamp.isValid ? timestamp.toFormat('hh:mm:ss a ZZZZ') : 'none';
}
